import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";

interface OrderSuccessProps {
  onClose: () => void;
  orderId?: string;
}

const OrderSuccess = ({ onClose, orderId }: OrderSuccessProps) => {
  const navigate = useNavigate();
  const { fetchCart } = useCart();

  // Refresh cart immediately when this modal appears
  useEffect(() => {
    const refreshCart = async () => {
      // Small delay to ensure backend has processed the order
      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchCart();
      // Dispatch cart update event to ensure all components refresh
      window.dispatchEvent(new Event('cartUpdated'));
    };
    refreshCart();
  }, [fetchCart]);

  const handleOkClick = async () => {
    // Refresh cart one more time before navigation to ensure it's up to date
    await fetchCart();
    window.dispatchEvent(new Event('cartUpdated'));
    
    onClose();
    navigate(orderId ? "/orders" : "/");
  };

  return (
    <div>
      {/* Backdrop Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 z-[1005] transition-colors duration-300" />

      {/* Modal Container */}
      <div className="fixed inset-0 flex items-center justify-center z-[1006] pointer-events-none">
        <div
          className="w-[480px] p-10 rounded-[24px] bg-white dark:bg-gray-800 pointer-events-auto relative transition-colors duration-300"
          style={{
            boxShadow: "-23px 23px 72px 0 rgba(0, 0, 0, 0.15)",
          }}
        >
          {/* Success Animation/Icon */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              {/* Outer pulsing circle */}
              <div className="absolute inset-0 rounded-full bg-green-400 opacity-25 animate-ping"></div>
              
              {/* Main circle */}
              <div className="relative bg-gradient-to-r from-green-400 to-green-500 rounded-full p-8">
                {/* Checkmark */}
                <svg
                  className="w-16 h-16 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Celebration Confetti Effect */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-[24px]">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 rounded-full animate-confetti"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `-10%`,
                  backgroundColor: [
                    "#FFD700",
                    "#FF6B6B",
                    "#4ECDC4",
                    "#95E1D3",
                    "#F38181",
                    "#AA96DA",
                  ][Math.floor(Math.random() * 6)],
                  animationDelay: `${Math.random() * 0.5}s`,
                  animationDuration: `${2 + Math.random() * 2}s`,
                }}
              />
            ))}
          </div>

          {/* Success Message */}
          <div className="text-center mb-8">
            <h2 className="text-[#050505] dark:text-white font-urbanist text-[28px] font-bold leading-[140%] mb-3">
              🎉 Order Placed Successfully!
            </h2>
            <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[16px] font-normal leading-[160%]">
              Your order has been confirmed and will be delivered soon.
            </p>
          </div>

          {/* OK Button */}
          <button
            onClick={handleOkClick}
            className="w-full py-4 px-6 bg-gradient-to-r from-[#7402A7] to-[#9b30cc] dark:from-purple-600 dark:to-purple-700 rounded-[150px] cursor-pointer hover:from-[#5f0289] hover:to-[#7402A7] dark:hover:from-purple-700 dark:hover:to-purple-800 transition-all transform hover:scale-105"
          >
            <span className="text-white text-center font-urbanist text-[20px] font-semibold leading-[140%]">
              OK
            </span>
          </button>
        </div>
      </div>

      <style>{`
        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti linear forwards;
        }
      `}</style>
    </div>
  );
};

export default OrderSuccess;
